import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../services/api';
import { Link } from 'react-router-dom';
import { Medal, TrendingUp, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';


const History = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter history based on search query (RFQ Number, Item, Supplier Name, Supplier Category)
  const filteredHistory = useMemo(() => {
    if (!searchQuery.trim()) return history;
    const query = searchQuery.toLowerCase();
    return history.filter(item => 
      item.rfq.rfq_number.toLowerCase().includes(query) ||
      item.rfq.item_name.toLowerCase().includes(query) ||
      (item.rfq.description && item.rfq.description.toLowerCase().includes(query)) ||
      (item.l1_supplier?.name && item.l1_supplier.name.toLowerCase().includes(query)) ||
      // Search in supplier categories
      (item.rfq.supplier_categories && item.rfq.supplier_categories.some(cat => cat.toLowerCase().includes(query))) ||
      // Search in supplier product types
      (item.rfq.supplier_product_types && item.rfq.supplier_product_types.some(pt => pt.toLowerCase().includes(query)))
    );
  }, [history, searchQuery]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await apiClient.get('/history');
      setHistory(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load history');
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96" data-testid="history-loading">Loading history...</div>;
  }

  return (
    <div data-testid="history-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="history-title">RFQ History</h1>
        <p className="text-sm text-slate-500 mt-2">Past RFQs with best suppliers (L1)</p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by RFQ, Item, Supplier, Category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="history-search-input"
          />
        </div>
        {searchQuery && (
          <p className="text-sm text-slate-500 mt-2">
            Showing {filteredHistory.length} of {history.length} records
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="history-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">RFQ Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Item</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Quotes Received</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">L1 Supplier</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">L1 Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredHistory.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500" data-testid="no-history-message">
                    {searchQuery ? 'No records match your search.' : 'No RFQ history available. Send RFQs and receive quotes to see history.'}
                  </td>
                </tr>
              ) : (
                filteredHistory.map((item) => (
                  <tr key={item.rfq.id} className="hover:bg-slate-50" data-testid={`history-row-${item.rfq.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono font-medium text-slate-900">{item.rfq.rfq_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-900">{item.rfq.item_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono text-sm text-slate-900">
                        {item.rfq.quantity} {item.rfq.unit}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="badge badge-info">{item.quote_count} quotes</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Medal className="text-yellow-600" size={16} />
                        <div>
                          <div className="text-sm font-medium text-slate-900">{item.l1_supplier.name}</div>
                          <div className="text-xs text-slate-500">{item.l1_supplier.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="font-mono font-semibold text-green-700">{item.l1_rate.toFixed(2)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {new Date(item.rfq.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/rfqs/${item.rfq.id}`} data-testid={`view-history-rfq-${item.rfq.id}`}>
                        <button className="text-sm text-slate-900 font-medium hover:text-slate-700">View →</button>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default History;